import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  MenuItem,
  Paper,
  TextField,
  Typography,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import { getAdminAlerts, type DashboardAlertItem } from '../services/adminDashboardApi';
import { useLanguage } from '../hooks/useLanguage';
import { ContentPageLayout } from '../components/Shared';

const AlertsDashboardPage: React.FC = () => {
  const { navigateWithLang } = useLanguage();
  const [days, setDays] = useState(30);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [items, setItems] = useState<DashboardAlertItem[]>([]);
  const [refreshedAt, setRefreshedAt] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await getAdminAlerts(days);
      setItems(res.data?.items || []);
      setRefreshedAt(res.data?.refreshedAt || null);
    } catch (e: any) {
      setError(e?.message || 'Failed to load alerts');
    } finally {
      setLoading(false);
    }
  }, [days]);

  useEffect(() => {
    load();
  }, [load]);

  const severity = (s: DashboardAlertItem['severity']) => {
    if (s === 'error') return 'error' as const;
    if (s === 'warning') return 'warning' as const;
    return 'info' as const;
  };

  return (
    <ContentPageLayout>
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="flex-start" flexWrap="wrap" gap={2} mb={2}>
        <Box>
          <Typography variant="h5" color="primary" fontWeight="bold">
            Alerts
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Operational queue: trials, payments, teachers, and data health.
          </Typography>
          {refreshedAt ? (
            <Typography variant="caption" color="text.secondary" display="block">
              Refreshed {new Date(refreshedAt).toLocaleString()}
            </Typography>
          ) : null}
        </Box>
        <Box display="flex" gap={1}>
          <TextField
            select
            size="small"
            label="Context period"
            value={days}
            onChange={(e) => setDays(Number(e.target.value))}
            sx={{ minWidth: 130 }}
          >
            <MenuItem value={7}>7 days</MenuItem>
            <MenuItem value={30}>30 days</MenuItem>
            <MenuItem value={90}>90 days</MenuItem>
          </TextField>
          <Button size="small" startIcon={<RefreshIcon />} onClick={load} disabled={loading}>
            Refresh
          </Button>
        </Box>
      </Box>

      {error ? <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert> : null}

      {loading ? (
        <Box display="flex" justifyContent="center" py={6}>
          <CircularProgress />
        </Box>
      ) : items.length === 0 ? (
        <Paper variant="outlined" sx={{ p: 4, textAlign: 'center' }}>
          <Typography color="text.secondary">No active alerts. Fleet looks healthy.</Typography>
        </Paper>
      ) : (
        <Box display="flex" flexDirection="column" gap={1.5}>
          {items.map((item) => (
            <Alert
              key={item.type}
              severity={severity(item.severity)}
              sx={{ cursor: item.link ? 'pointer' : 'default' }}
              onClick={() => item.link && navigateWithLang(item.link)}
            >
              <Typography fontWeight={600}>{item.label}</Typography>
              {item.link ? (
                <Typography variant="caption" display="block" sx={{ mt: 0.5 }}>
                  Click to investigate
                </Typography>
              ) : null}
            </Alert>
          ))}
        </Box>
      )}
    </Box>
    </ContentPageLayout>
  );
};

export default AlertsDashboardPage;
